import {AxiosError, AxiosResponse} from 'axios';
import {t} from 'i18next';
import {
  MutableRefObject,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import {
  createECACustomField,
  createECAMetaDataField,
  ECAFieldTags,
  getECAFieldDetail,
  updateECAFieldDetail,
} from '../../../../api/EcaServices';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {RootState} from '../../../../redux/store';
import {FieldDropDownType as FieldTypes} from '../../../../utils/fieldManagementUtils';
import {ITag} from './DraggableTag';
import {FieldCategoryType, FieldValues} from './FieldView';
import {FieldType, IFieldModal} from './constant';

const SUCCESS_STATUS_200 = 200;
const SUCCESS_STATUS_201 = 201;
const DUPLICATE_STATUS_409 = 409;
const USED_STATUS_403 = 403;

interface IUseCustomFieldReturn {
  fieldRef: MutableRefObject<IFieldModal | undefined>;
  loading?: boolean;
  duplicateError?: boolean;
  duplicateTagError?: boolean;
  usedTagError?: boolean;
  createField: (name: string) => Promise<boolean>;
  updateField: (
    param: Record<string, string | boolean>,
    successMsg?: string
  ) => Promise<boolean>;
  addTag: (name: string, displayOrder: number) => Promise<boolean>;
  deleteTag: (tagId: number) => Promise<boolean>;
  updateTag: (tags: ITag[]) => Promise<boolean>;
  clearDuplicateError?: () => void;
  clearDuplicateTagError?: () => void;
  clearUsedTagError?: () => void;
  createMetaDataField: (value: FieldValues) => Promise<boolean>;
}

export const useCustomField = (): IUseCustomFieldReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [duplicateError, setDuplicateError] = useState<boolean>(false);
  const [duplicateTagError, setDuplicateTagError] = useState<boolean>(false);
  const [usedTagError, setUsedTagError] = useState<boolean>(false);
  const fieldRef = useRef<IFieldModal>();
  const {showToast} = useToast();

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const projectId = useSelector((state: RootState) => state.reports).projectId;

  const showSuccessMessage = useCallback(
    (message: string) => {
      showToast(message, 'success', 3000, 'center', 'bottom');
    },
    [showToast]
  );

  const showErrorMessage = useCallback(
    (message: string) => {
      showToast(message, 'error', 3000, 'center', 'bottom');
    },
    [showToast]
  );

  const getFieldDetail = useCallback(
    (fieldId: number): Promise<IFieldModal | undefined> => {
      return new Promise((resolve) => {
        if (projectId) {
          getECAFieldDetail(fieldId, clientId, projectId)
            .then(({status, data}: AxiosResponse) => {
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                resolve(data.data);
              } else {
                resolve(undefined);
              }
            })
            .catch(() => {
              resolve(undefined);
            });
        }
      });
    },
    [clientId, projectId]
  );

  const handleUpdateFieldDetails = useCallback(
    (
      fieldId: number,
      successMessage: string,
      resolve: (value: boolean | PromiseLike<boolean>) => void,
      createData?: {
        fieldLabel: string;
        fieldCategory: FieldCategoryType;
        fieldUIType: FieldType;
      }
    ) => {
      getFieldDetail(fieldId)
        .then((fieldResponse) => {
          setLoading(false);
          if (fieldResponse) {
            fieldRef.current = fieldResponse;
          } else if (createData) {
            fieldRef.current = {
              ...createData,
              fieldId,
              tagValues: [],
            };
          }
          showSuccessMessage(successMessage);
          resolve(true);
        })
        .catch(() => setLoading(false));
    },
    [getFieldDetail, showSuccessMessage]
  );

  const handleError = useCallback(
    (
      error: AxiosError,
      reject: (reason?: unknown) => void,
      stateAction?: (value: SetStateAction<boolean>) => void
    ) => {
      setLoading(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = error.response?.data as any;
      if (
        error.status === DUPLICATE_STATUS_409 ||
        error.status === USED_STATUS_403
      ) {
        stateAction?.(true);
      } else if (data?.meta?.message) {
        showErrorMessage(data.meta?.message);
      } else {
        showErrorMessage(t('common.somethingWrong'));
      }

      reject(false);
    },
    [showErrorMessage]
  );

  const updateField = useCallback(
    (
      param: Record<string, string | boolean>,
      successMsg: string = t(
        'fieldManagement.customField.fieldSaveChangesAdded'
      )
    ): Promise<boolean> => {
      setLoading(true);
      return new Promise((resolve, reject) => {
        if (projectId && fieldRef.current) {
          const fieldId = fieldRef.current.fieldId;
          updateECAFieldDetail(clientId, projectId, {
            id: fieldId,
            ...param,
          })
            .then(({status}: AxiosResponse) => {
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                handleUpdateFieldDetails(fieldId, successMsg, resolve);
              }
            })
            .catch((error: AxiosError) => {
              handleError(error, reject, setDuplicateError);
            });
        }
      });
    },
    [clientId, handleError, handleUpdateFieldDetails, projectId]
  );

  const createField = useCallback(
    (name: string): Promise<boolean> => {
      setLoading(true);
      return new Promise((resolve, reject) => {
        if (projectId) {
          createECACustomField(clientId, projectId, {label: name})
            .then(({status, data}: AxiosResponse) => {
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                const fieldId = data.data;
                handleUpdateFieldDetails(
                  fieldId,
                  t('fieldManagement.customField.fieldAdded'),
                  resolve,
                  {
                    fieldLabel: name,
                    fieldCategory: 'TAG_FIELD',
                    fieldUIType: 'Tag',
                  }
                );
              }
            })
            .catch((error: AxiosError) => {
              handleError(error, reject, setDuplicateError);
            });
        }
      });
    },
    [clientId, handleError, handleUpdateFieldDetails, projectId]
  );

  const addTag = useCallback(
    (name: string, displayOrder: number): Promise<boolean> => {
      setLoading(true);
      return new Promise((resolve, reject) => {
        if (projectId && fieldRef.current) {
          const fieldId = fieldRef.current?.fieldId;
          ECAFieldTags('post', fieldId, clientId, projectId, [
            {
              value: name,
              displayOrder,
            },
          ])
            .then(({status}: AxiosResponse) => {
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                handleUpdateFieldDetails(
                  fieldId,
                  t('fieldManagement.customField.tagAdded'),
                  resolve
                );
              }
            })
            .catch((error: AxiosError) => {
              handleError(error, reject, setDuplicateTagError);
            });
        }
      });
    },
    [clientId, handleError, handleUpdateFieldDetails, projectId]
  );

  const updateTag = useCallback(
    (tags: ITag[]): Promise<boolean> => {
      setLoading(true);
      return new Promise((resolve, reject) => {
        if (projectId && fieldRef.current) {
          const fieldId = fieldRef.current?.fieldId;
          const updateTags = tags.map((tag) => {
            return {
              id: tag.tagValueId,
              value: tag.tagValue,
              displayOrder: tag.displayOrder,
            };
          });
          ECAFieldTags('put', fieldId, clientId, projectId, updateTags as never)
            .then(({status}: AxiosResponse) => {
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                handleUpdateFieldDetails(
                  fieldId,
                  t('fieldManagement.customField.fieldSaveChangesAdded'),
                  resolve
                );
              }
            })
            .catch((error: AxiosError) => {
              handleError(error, reject, setUsedTagError);
            });
        }
      });
    },
    [clientId, handleError, handleUpdateFieldDetails, projectId]
  );

  const deleteTag = useCallback(
    (id: number): Promise<boolean> => {
      setLoading(true);
      return new Promise((resolve, reject) => {
        if (projectId && fieldRef.current) {
          const fieldId = fieldRef.current?.fieldId;
          ECAFieldTags('delete', fieldId, clientId, projectId, [
            {
              id,
            },
          ])
            .then(({status}: AxiosResponse) => {
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                handleUpdateFieldDetails(
                  fieldId,
                  t('fieldManagement.customField.tagDeleted'),
                  resolve
                );
              }
            })
            .catch((error: AxiosError) => {
              handleError(error, reject, setUsedTagError);
            });
        }
      });
    },
    [clientId, handleError, handleUpdateFieldDetails, projectId]
  );

  const getFieldDateFormat = (fieldType: string) => {
    switch (fieldType) {
      case FieldTypes.Date:
        return 'yyyy/MM/dd';
      case FieldTypes.Time:
        return 'HH:mm:ss';
      case FieldTypes.DateTime:
        return 'yyyy/MM/dd HH:mm:ss';
      default:
        return null;
    }
  };

  const createMetaDataField = useCallback(
    (values: FieldValues): Promise<boolean> => {
      setLoading(true);
      return new Promise((resolve, reject) => {
        if (projectId) {
          createECAMetaDataField(clientId, projectId, {
            label: values.fieldName,
            fieldType: values.fieldType,
            fieldDateFormat: getFieldDateFormat(values.fieldType || ''),
            isMultiValueField: !!values.isMultiValueField,
            isRelationShipField: !!values.isRelationShipField,
          })
            .then(({status}: AxiosResponse) => {
              setLoading(false);
              if (
                status === SUCCESS_STATUS_200 ||
                status === SUCCESS_STATUS_201
              ) {
                showSuccessMessage(
                  t('fieldManagement.customField.metaDataFieldAdded')
                );
                resolve(true);
              }
            })
            .catch((error: AxiosError) => {
              handleError(error, reject, setDuplicateError);
            });
        }
      });
    },
    [clientId, handleError, projectId, showSuccessMessage]
  );

  const clearDuplicateError = useCallback(() => {
    setDuplicateError(false);
  }, []);

  const clearDuplicateTagError = useCallback(() => {
    setDuplicateTagError(false);
  }, []);

  const clearUsedTagError = useCallback(() => {
    setDuplicateTagError(false);
  }, []);

  return {
    fieldRef,
    loading,
    duplicateError,
    duplicateTagError,
    usedTagError,
    createField,
    updateField,
    addTag,
    updateTag,
    deleteTag,
    clearDuplicateError,
    clearDuplicateTagError,
    clearUsedTagError,
    createMetaDataField,
  };
};
